import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useL10n } from '@uplift/hooks';
import { DateObject } from '@uplift/services';
import { Spinner, Scheduler, useAlert } from '@uplift/ui-v2';
import { useMemo } from 'react';
import { ConfirmDialog } from './confirm.dialog';
import { useFindPsychiatristStyles } from './styles';
import { ViewByChip } from './view-by-chip';
import { MatchingProfileData } from '@uplift/api';

export type FindPsychiatristProps = {
  appointmentTime: DateObject;
  currentProvider: MatchingProfileData | null;
  providers: MatchingProfileData[];
  selectedProvider: MatchingProfileData | null;
  timeSlots: DateObject[];
  onDialogClose: (confirm?: boolean) => Promise<void>;
  onProviderSelect: (provider: MatchingProfileData | null) => void;
  onScheduleSession: (selectedSlot: DateObject) => void | Promise<void>;
  isLoading?: boolean;
};

export const FindPsychiatrist: React.VFC<FindPsychiatristProps> = ({
  appointmentTime,
  currentProvider,
  providers,
  selectedProvider,
  timeSlots,
  onDialogClose,
  onProviderSelect,
  onScheduleSession,
  isLoading
}) => {
  const l10n = useL10n();
  const { classes } = useFindPsychiatristStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const snackbar = useAlert();
  const userName = currentProvider?.nameDisplay;

  const handleDisabledClick = () => {
    snackbar.info(l10n.findPsychiatrist.tooFar, { preventDuplicate: true });
  };

  const loading = isLoading ?? false;

  const displayName = useMemo(() => {
    if (loading || !userName) {
      return '';
    }

    return userName.defaultDisplay;
  }, [ loading, userName ]);


  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h3">
            {l10n.findPsychiatrist.title}
          </Typography>
          {currentProvider && (
            <Box>
              <ViewByChip
                providers={providers}
                currentProvider={currentProvider}
                onProviderSelect={onProviderSelect}
              />
            </Box>
          )}
        </Box>
        <Box>
          <Spinner />
        </Box>
      </Box>
    );
  }


  if (!timeSlots.length && !loading) {
    const topLine = currentProvider
      ? displayName
      : l10n.findPsychiatrist.emptyStateAllProviders;

    return (
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h3">
            {l10n.findPsychiatrist.title}
          </Typography>
          <Box>
            <ViewByChip
              providers={providers}
              currentProvider={currentProvider}
              onProviderSelect={onProviderSelect}
            />
          </Box>
        </Box>
        <Box className={`${classes.body} error`}>
          <Typography className={classes.emptyState}>
            <p>{topLine}</p>
            <p dangerouslySetInnerHTML={{ __html: l10n.findPsychiatrist.emptyStateLine2 }} />
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h3">
            {l10n.findPsychiatrist.title}
          </Typography>
          <Box>
            <ViewByChip
              providers={providers}
              currentProvider={currentProvider}
              onProviderSelect={onProviderSelect}
            />
          </Box>
        </Box>
        <Scheduler
          dayParting
          timeslots={timeSlots}
          withDatePicker={isDesktop}
          onScheduleSession={onScheduleSession}
          daysAheadLimit={45}
          hideTimezoneSelect={isDesktop}
          onDisabledForwardClick={handleDisabledClick}
        />
      </Box>
      {selectedProvider && (
        <ConfirmDialog
          provider={selectedProvider}
          appointmentTime={appointmentTime}
          isLoading={isLoading ?? false}
          onClose={onDialogClose}
        />
      )}
    </>
  );
};
